// Browser notifications (Web Push): the service worker, the subscription and its device on
// the server (docs/spec.md, Notifications push). The choice to turn them off is kept per
// browser, since a subscription belongs to a browser, not to an account.

import { api } from './api';

const DEVICE_KEY = 'push-device';
const OFF_KEY = 'push-off';

export type PushState =
  /** The browser cannot (no service worker, no Push API, or not a secure context). */
  | 'unsupported'
  /** The server has no VAPID key: browser notifications are off for everyone. */
  | 'unavailable'
  /** The user refused in the browser: only the browser's settings can undo it. */
  | 'denied'
  | 'enabled'
  | 'disabled';

function storage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private browsing: the choice lasts as long as the page.
  }
}

export function supported(): boolean {
  return isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let registration: Promise<ServiceWorkerRegistration> | null = null;

/** Registers the service worker once; `navigate` opens a URL when a notification is clicked. */
export function startWorker(navigate: (url: string) => void): void {
  if (!('serviceWorker' in navigator) || registration) return;
  registration = navigator.serviceWorker.register('/sw.js');
  registration.catch(() => {
    registration = null;
  });
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data?.type === 'open' && typeof data.url === 'string' && data.url.startsWith('/')) navigate(data.url);
  });
}

async function worker(): Promise<ServiceWorkerRegistration> {
  if (!registration) registration = navigator.serviceWorker.register('/sw.js');
  await registration;
  return navigator.serviceWorker.ready;
}

/** The key as `PushManager.subscribe` wants it. */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((b, i) => b === key[i]);
}

export async function pushState(publicKey: string | null | undefined): Promise<PushState> {
  if (!supported()) return 'unsupported';
  if (!publicKey) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted' || storage(OFF_KEY)) return 'disabled';
  const subscription = await (await worker()).pushManager.getSubscription();
  return subscription && storage(DEVICE_KEY) ? 'enabled' : 'disabled';
}

/** Subscribes this browser (or renews its subscription) and gives it to the server. */
async function subscribe(publicKey: string): Promise<void> {
  const key = decodeKey(publicKey);
  const manager = (await worker()).pushManager;
  let subscription = await manager.getSubscription();
  if (subscription && !sameKey(subscription, key)) {
    // The server's key changed: the old subscription can no longer be used.
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await manager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const device = await api.registerDevice('web', JSON.stringify(subscription.toJSON()));
  store(DEVICE_KEY, device.id);
}

/** From a click: asks the browser's permission, then subscribes. */
export async function enablePush(publicKey: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'disabled';
  store(OFF_KEY, null);
  await subscribe(publicKey);
  return 'enabled';
}

export async function disablePush(): Promise<PushState> {
  store(OFF_KEY, '1');
  await forgetDevice();
  const subscription = await (await worker()).pushManager.getSubscription();
  await subscription?.unsubscribe();
  return 'disabled';
}

/** After login: when this browser already allows notifications, its device is renewed and
 *  moves to the account now logged in. */
export async function resumePush(publicKey: string | null | undefined): Promise<void> {
  if (!supported() || !publicKey || Notification.permission !== 'granted' || storage(OFF_KEY)) return;
  await subscribe(publicKey);
}

/** At logout: this browser stops receiving the account's notifications. */
export async function forgetDevice(): Promise<void> {
  const id = storage(DEVICE_KEY);
  if (!id) return;
  store(DEVICE_KEY, null);
  await api.removeDevice(id).catch(() => {});
}
